import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import api, { TRADE } from '../../infrastructure/config/api';
import Button from '../components/common/Button';

function formatValue(value) {
  if (value === null || value === undefined) return 'null';
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function getStatusColor(status) {
  if (status === 'submitted') return 'success';
  if (status === 'queued') return 'info';
  if (status === 'filtered' || status === 'duplicate' || status === 'skipped_runtime') return 'warning';
  if (status === 'error') return 'error';
  return 'default';
}

const V18_ENTRY_FILTER_DEFAULTS = {
  minScore: 6,
  allowedGrades: ['PRIME', 'HIGH'],
  allowedSweeps: ['GRIND', 'FLUSH', 'MAJOR', 'MINOR'],
  minVolRatio: 1.2,
  minGradient: 0.5,
  minZoneTouches: 2,
  minAtrPct: 0.15,
  requireVacuum: false,
  requireTrendAllowed: true,
  requirePrimeEnabled: false,
  strictFourLayer: true,
};

const V18_XAG_TRADE_DEFAULTS = {
  leverage: 15,
  investment: '',
};

const numberFields = [
  ['minScore', 'Min score'],
  ['minVolRatio', 'Min volume ratio'],
  ['minGradient', 'Min gradient'],
  ['minZoneTouches', 'Min zone touches'],
  ['minAtrPct', 'Min ATR %'],
];

const booleanFields = [
  ['requireVacuum', 'Require vacuum'],
  ['requireTrendAllowed', 'Require trend allowed'],
  ['requirePrimeEnabled', 'Require prime filter enabled'],
  ['strictFourLayer', 'Strict four layer'],
];

function listToText(value) {
  return Array.isArray(value) ? value.join(', ') : String(value || '');
}

function textToList(value) {
  return String(value || '')
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
}

const V18InspectorPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const [loading, setLoading] = useState(false);
  const [limit, setLimit] = useState('50');
  const [events, setEvents] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [filterSettings, setFilterSettings] = useState(V18_ENTRY_FILTER_DEFAULTS);
  const [xagSettingsLoading, setXagSettingsLoading] = useState(false);
  const [xagSettingsSaving, setXagSettingsSaving] = useState(false);
  const [xagTradeSettings, setXagTradeSettings] = useState(V18_XAG_TRADE_DEFAULTS);

  const isAllowed = currentUser?.role === 'admin' && currentUser?.username === 'devk';
  const selectedEvent = events.find((item) => item.id === selectedEventId) || events[0] || null;

  const loadFilterSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await api.get(TRADE.V18_ENTRY_FILTER_SETTINGS);
      setFilterSettings({
        ...V18_ENTRY_FILTER_DEFAULTS,
        ...(response.data?.data || {}),
      });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to load v18 entry filter settings', {
        variant: 'error',
      });
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadXagTradeSettings = async () => {
    setXagSettingsLoading(true);
    try {
      const response = await api.get(TRADE.V18_XAG_TRADE_SETTINGS);
      setXagTradeSettings({
        ...V18_XAG_TRADE_DEFAULTS,
        ...(response.data?.data || {}),
      });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to load v18 XAG trade settings', {
        variant: 'error',
      });
    } finally {
      setXagSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (isAllowed) {
      loadFilterSettings();
      loadXagTradeSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAllowed]);

  const updateFilterSetting = (key, value) => {
    setFilterSettings((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const saveFilterSettings = async () => {
    setSettingsSaving(true);
    try {
      const payload = {
        ...filterSettings,
        minScore: Number(filterSettings.minScore),
        minVolRatio: Number(filterSettings.minVolRatio),
        minGradient: Number(filterSettings.minGradient),
        minZoneTouches: Number(filterSettings.minZoneTouches),
        minAtrPct: Number(filterSettings.minAtrPct),
        allowedGrades: textToList(filterSettings.allowedGrades),
        allowedSweeps: textToList(filterSettings.allowedSweeps),
      };
      const response = await api.put(TRADE.V18_ENTRY_FILTER_SETTINGS, payload);
      setFilterSettings({
        ...V18_ENTRY_FILTER_DEFAULTS,
        ...(response.data?.data || payload),
      });
      enqueueSnackbar('Saved v18 entry filter settings', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to save v18 entry filter settings', {
        variant: 'error',
      });
    } finally {
      setSettingsSaving(false);
    }
  };

  const updateXagTradeSetting = (key, value) => {
    setXagTradeSettings((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const saveXagTradeSettings = async () => {
    setXagSettingsSaving(true);
    try {
      const payload = {
        leverage: Number(xagTradeSettings.leverage),
        investment: xagTradeSettings.investment === ''
          ? null
          : Number(xagTradeSettings.investment),
      };
      const response = await api.put(TRADE.V18_XAG_TRADE_SETTINGS, payload);
      setXagTradeSettings({
        ...V18_XAG_TRADE_DEFAULTS,
        ...(response.data?.data || payload),
      });
      enqueueSnackbar('Saved v18 XAG trade settings', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to save v18 XAG trade settings', {
        variant: 'error',
      });
    } finally {
      setXagSettingsSaving(false);
    }
  };

  const loadRecentAlerts = async () => {
    setLoading(true);
    try {
      const response = await api.get(TRADE.V18_EVENTS, {
        params: { limit: Number(limit) || 50 },
      });
      const rows = response.data?.data || [];
      setEvents(rows);
      setSelectedEventId(rows[0]?.id || '');
    } catch (error) {
      enqueueSnackbar(error.response?.data?.message || 'Failed to load v18 events', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isAllowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Container
      maxWidth="xl"
      sx={{
        mt: { xs: 2, md: 4 },
        mb: 6,
        px: { xs: 1.5, sm: 3 },
      }}
    >
      <Stack spacing={3}>
        <Box>
          <Typography variant={isMobile ? 'h4' : 'h3'} fontWeight={700}>V18 Alert Monitor</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Bấm load để xem các alert `v18` gần nhất server đã nhận, trạng thái xử lý và lý do bị skip hoặc lỗi.
          </Typography>
        </Box>

        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
              justifyContent: { xs: 'center', sm: 'flex-start' },
            }}
          >
            <TextField
              label="Recent alerts"
              size="small"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              sx={{ width: { xs: '100%', sm: 140 }, maxWidth: 220 }}
            />
            <Button
              variant="contained"
              onClick={loadRecentAlerts}
              loading={loading}
              sx={{ width: { xs: '100%', sm: 'auto' }, maxWidth: { xs: 320, sm: 'none' } }}
            >
              Load recent alerts
            </Button>
          </Box>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6">Entry Filter Settings</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Cấu hình này được lưu vào server và dùng cho cả webhook live lẫn inspect.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              {numberFields.map(([key, label]) => (
                <Grid item xs={12} sm={6} md={2.4} key={key}>
                  <TextField
                    fullWidth
                    type="number"
                    label={label}
                    size="small"
                    value={filterSettings[key]}
                    onChange={(e) => updateFilterSetting(key, e.target.value)}
                    inputProps={{ step: key === 'minScore' || key === 'minZoneTouches' ? 1 : 0.01 }}
                  />
                </Grid>
              ))}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Allowed grades"
                  size="small"
                  value={listToText(filterSettings.allowedGrades)}
                  onChange={(e) => updateFilterSetting('allowedGrades', e.target.value)}
                  helperText="Comma separated, ví dụ: PRIME, HIGH"
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Allowed sweeps"
                  size="small"
                  value={listToText(filterSettings.allowedSweeps)}
                  onChange={(e) => updateFilterSetting('allowedSweeps', e.target.value)}
                  helperText="Comma separated, ví dụ: GRIND, FLUSH, MAJOR, MINOR"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 2 }, flexWrap: 'wrap' }}>
              {booleanFields.map(([key, label]) => (
                <FormControlLabel
                  key={key}
                  control={
                    <Switch
                      checked={Boolean(filterSettings[key])}
                      onChange={(e) => updateFilterSetting(key, e.target.checked)}
                    />
                  }
                  label={label}
                />
              ))}
            </Box>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={saveFilterSettings}
                loading={settingsSaving}
                disabled={settingsLoading}
              >
                Save settings
              </Button>
              <Button
                variant="outlined"
                onClick={loadFilterSettings}
                loading={settingsLoading}
                disabled={settingsSaving}
              >
                Reload
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="h6">XAG Trade Settings</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Chỉ áp dụng cho XAGUSDT. Các symbol khác vẫn dùng sizing mặc định.
              </Typography>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="XAG leverage"
                  size="small"
                  value={xagTradeSettings.leverage}
                  onChange={(e) => updateXagTradeSetting('leverage', e.target.value)}
                  inputProps={{ min: 1, step: 1 }}
                  helperText="Default 15x"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  type="number"
                  label="XAG investment (USDT)"
                  size="small"
                  value={xagTradeSettings.investment ?? ''}
                  onChange={(e) => updateXagTradeSetting('investment', e.target.value)}
                  inputProps={{ min: 0.01, step: 0.1 }}
                  helperText="Để trống = dùng V17 Investment"
                />
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                onClick={saveXagTradeSettings}
                loading={xagSettingsSaving}
                disabled={xagSettingsLoading}
              >
                Save XAG settings
              </Button>
              <Button
                variant="outlined"
                onClick={loadXagTradeSettings}
                loading={xagSettingsLoading}
                disabled={xagSettingsSaving}
              >
                Reload
              </Button>
            </Box>
          </Stack>
        </Paper>

        <Grid container columnSpacing={{ xs: 0, md: 3 }} rowSpacing={3}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: { xs: 1.5, md: 2 } }}>
              <Typography variant="h6" sx={{ mb: 2 }}>Recent Alerts</Typography>
              {isMobile ? (
                <List disablePadding>
                  {events.map((event) => (
                    <ListItem key={event.id} disablePadding sx={{ mb: 1 }}>
                      <ListItemButton
                        selected={event.id === selectedEventId}
                        onClick={() => setSelectedEventId(event.id)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 2,
                          alignItems: 'flex-start',
                          py: 1.25,
                        }}
                      >
                        <Stack spacing={1} sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Typography variant="subtitle2">{event.symbol || 'N/A'}</Typography>
                            <Chip size="small" label={event.status || 'N/A'} color={getStatusColor(event.status)} />
                          </Box>
                          <Typography variant="body2">{event.comment || 'N/A'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {event.recordedAt?.replace('T', ' ').slice(0, 19) || 'N/A'}
                          </Typography>
                        </Stack>
                      </ListItemButton>
                    </ListItem>
                  ))}
                  {events.length === 0 && (
                    <Typography color="text.secondary">Chưa có event nào. Bấm `Load recent alerts`.</Typography>
                  )}
                </List>
              ) : (
                <TableContainer sx={{ maxHeight: 560, overflowX: 'auto' }}>
                  <Table stickyHeader size="small" sx={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Symbol</TableCell>
                        <TableCell>Comment</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {events.map((event) => (
                        <TableRow
                          key={event.id}
                          hover
                          selected={event.id === selectedEventId}
                          onClick={() => setSelectedEventId(event.id)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{event.recordedAt?.replace('T', ' ').slice(0, 19) || 'N/A'}</TableCell>
                          <TableCell>{event.symbol || 'N/A'}</TableCell>
                          <TableCell>{event.comment || 'N/A'}</TableCell>
                          <TableCell>
                            <Chip size="small" label={event.status || 'N/A'} color={getStatusColor(event.status)} />
                          </TableCell>
                          <TableCell>{event.action || 'N/A'}</TableCell>
                        </TableRow>
                      ))}
                      {events.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5}>Chưa có event nào. Bấm `Load recent alerts`.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={5}>
            <Paper sx={{ p: { xs: 2, md: 3 }, minHeight: { xs: 'auto', md: 560 } }}>
              {!selectedEvent ? (
                <Typography color="text.secondary">Chọn một alert để xem chi tiết.</Typography>
              ) : (
                <Stack spacing={2}>
                  <Alert severity={selectedEvent.accepted ? 'success' : selectedEvent.status === 'error' ? 'error' : 'warning'}>
                    {selectedEvent.message}
                  </Alert>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip label={`Status: ${selectedEvent.status || 'N/A'}`} color={getStatusColor(selectedEvent.status)} />
                    <Chip label={`Action: ${selectedEvent.action || 'N/A'}`} />
                    <Chip label={`Symbol: ${selectedEvent.symbol || 'N/A'}`} />
                    <Chip label={`Comment: ${selectedEvent.comment || 'N/A'}`} />
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="h6" gutterBottom>Reasons</Typography>
                    {selectedEvent.reasons?.length ? (
                      <List dense>
                        {selectedEvent.reasons.map((reason) => (
                          <ListItemButton key={reason} disableGutters>
                            <ListItemText primary={reason} />
                          </ListItemButton>
                        ))}
                      </List>
                    ) : (
                      <Typography color="text.secondary">Không có lý do chặn.</Typography>
                    )}
                  </Box>

                  <Divider />

                  <Box>
                    <Typography variant="h6" gutterBottom>Signal</Typography>
                    <Stack spacing={1}>
                      {Object.entries(selectedEvent.signal || {}).map(([key, value]) => (
                        <Paper variant="outlined" sx={{ p: 1.25 }} key={key}>
                          <Typography variant="caption" color="text.secondary">{key}</Typography>
                          <Typography variant="body2">{formatValue(value)}</Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Stack>
    </Container>
  );
};

export default V18InspectorPage;
